import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

import userService from "./userService";

export const getUsers = createAsyncThunk(
  "users/getUsers",
  async (params = {}, thunkAPI) => {
    try {
      return await userService.getUsers(params);
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  },
);

export const getUserById = createAsyncThunk(
  "users/getUserById",
  async (id, thunkAPI) => {
    try {
      return await userService.getUserById(id);
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  },
);

export const createUser = createAsyncThunk(
  "users/createUser",
  async (payload, thunkAPI) => {
    try {
      return await userService.createUser(payload);
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  },
);

export const updateUser = createAsyncThunk(
  "users/updateUser",
  async ({ id, data }, thunkAPI) => {
    try {
      return await userService.updateUser({ id, data });
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  },
);

export const updateUserPermissions = createAsyncThunk(
  "users/updateUserPermissions",
  async ({ id, permissions }, thunkAPI) => {
    try {
      return await userService.updateUserPermissions({ id, permissions });
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  },
);

export const changeUserStatus = createAsyncThunk(
  "users/changeUserStatus",
  async ({ id, status }, thunkAPI) => {
    try {
      return await userService.changeUserStatus({ id, status });
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  },
);

export const disableUser = createAsyncThunk(
  "users/disableUser",
  async (id, thunkAPI) => {
    try {
      return await userService.disableUser(id);
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  },
);

export const resendSetupInvite = createAsyncThunk(
  "users/resendSetupInvite",
  async (id, thunkAPI) => {
    try {
      return await userService.resendSetupInvite(id);
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  },
);

const initialState = {
  users: [],
  selectedUser: null,

  pagination: {
    page: 1,
    limit: 20,
    total: 0,
    pages: 0,
  },

  filters: {
    search: "",
    role: "",
    status: "",
    auth_provider: "",
  },

  isLoading: false,
  isCreating: false,
  isUpdating: false,
  isInviting: false,

  error: null,
};

const upsertUser = (users, updatedUser) => {
  if (!updatedUser?._id) return users;

  const exists = users.some((user) => user._id === updatedUser._id);

  if (!exists) return [updatedUser, ...users];

  return users.map((user) =>
    user._id === updatedUser._id ? updatedUser : user,
  );
};

const userSlice = createSlice({
  name: "users",

  initialState,

  reducers: {
    clearUserError: (state) => {
      state.error = null;
    },

    setSelectedUser: (state, action) => {
      state.selectedUser = action.payload;
    },

    clearSelectedUser: (state) => {
      state.selectedUser = null;
    },

    setUserFilters: (state, action) => {
      state.filters = {
        ...state.filters,
        ...action.payload,
      };
    },

    resetUserFilters: (state) => {
      state.filters = initialState.filters;
    },
  },

  extraReducers: (builder) => {
    builder
      .addCase(getUsers.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })

      .addCase(getUsers.fulfilled, (state, action) => {
        state.isLoading = false;

        const data = action.payload?.data;

        state.users = data?.items || [];
        state.pagination = data?.pagination || initialState.pagination;
      })

      .addCase(getUsers.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || "Gebruikers laden mislukt.";
      })

      .addCase(getUserById.fulfilled, (state, action) => {
        state.selectedUser = action.payload?.data || null;
      })

      .addCase(createUser.pending, (state) => {
        state.isCreating = true;
        state.error = null;
      })

      .addCase(createUser.fulfilled, (state, action) => {
        state.isCreating = false;

        const createdUser = action.payload?.data;

        if (createdUser) {
          state.users = upsertUser(state.users, createdUser);
        }
      })

      .addCase(createUser.rejected, (state, action) => {
        state.isCreating = false;
        state.error = action.payload || "Gebruiker aanmaken mislukt.";
      })

      .addCase(updateUser.pending, (state) => {
        state.isUpdating = true;
        state.error = null;
      })

      .addCase(updateUser.fulfilled, (state, action) => {
        state.isUpdating = false;

        const updatedUser = action.payload?.data;

        if (updatedUser) {
          state.users = upsertUser(state.users, updatedUser);

          if (state.selectedUser?._id === updatedUser._id) {
            state.selectedUser = updatedUser;
          }
        }
      })

      .addCase(updateUser.rejected, (state, action) => {
        state.isUpdating = false;
        state.error = action.payload || "Gebruiker wijzigen mislukt.";
      })

      .addCase(updateUserPermissions.fulfilled, (state, action) => {
        const updatedUser = action.payload?.data;

        if (updatedUser) {
          state.users = upsertUser(state.users, updatedUser);
        }
      })

      .addCase(changeUserStatus.fulfilled, (state, action) => {
        const updatedUser = action.payload?.data;

        if (updatedUser) {
          state.users = upsertUser(state.users, updatedUser);
        }
      })

      .addCase(disableUser.fulfilled, (state, action) => {
        const updatedUser = action.payload?.data;

        if (updatedUser) {
          state.users = upsertUser(state.users, updatedUser);
        }
      })

      .addCase(resendSetupInvite.pending, (state) => {
        state.isInviting = true;
        state.error = null;
      })

      .addCase(resendSetupInvite.fulfilled, (state) => {
        state.isInviting = false;
      })

      .addCase(resendSetupInvite.rejected, (state, action) => {
        state.isInviting = false;
        state.error = action.payload || "Uitnodiging verzenden mislukt.";
      });
  },
});

export const {
  clearUserError,
  setSelectedUser,
  clearSelectedUser,
  setUserFilters,
  resetUserFilters,
} = userSlice.actions;

export default userSlice.reducer;
